'use client';

import { Link } from '@tanstack/react-router';
import { Building2 } from 'lucide-react';
import { ErrorState, LoadingState, PageHeader } from '@/components/shared';
import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui';
import { usePartner } from '@/hooks/usePartners';
import { ChannelsCard } from './ChannelsCard';
import { CredentialCard } from './CredentialCard';
import { PoolTable } from './PoolTable';

/**
 * One partner: its token, its channels, its pool and its customers — in the
 * order an onboarding actually goes. Store the token, sync, allocate.
 */
export function PartnerDetailPage({ partnerId }: { partnerId: string }) {
  const { data: partner, isLoading, error } = usePartner(partnerId);

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState title="Could not load the partner" message={error.message} />;
  if (!partner) return <ErrorState title="Partner not found" message="It may have been deleted." />;

  return (
    <>
      <PageHeader
        backTo="/partners"
        backLabel="All partners"
        title={partner.name}
        description={`${partner.parentId ? 'Sub-dealer' : 'Integrator'} · ${partner.slug}${
          partner.contactEmail ? ` · ${partner.contactEmail}` : ''
        }`}
        actions={
          <Badge variant={partner.isActive ? 'success' : 'secondary'}>
            {partner.isActive ? 'Active' : 'Inactive'}
          </Badge>
        }
      />

      <Tabs defaultValue="pool">
        <TabsList>
          <TabsTrigger value="pool">Pool ({partner.stats.free} free)</TabsTrigger>
          <TabsTrigger value="token">Token</TabsTrigger>
          <TabsTrigger value="channels">Channels</TabsTrigger>
          <TabsTrigger value="customers">Customers ({partner.organizations.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="pool" className="pt-4">
          <PoolTable partner={partner} />
        </TabsContent>

        <TabsContent value="token" className="pt-4">
          <CredentialCard partner={partner} />
        </TabsContent>

        <TabsContent value="channels" className="pt-4">
          <ChannelsCard partnerId={partner.id} />
        </TabsContent>

        <TabsContent value="customers" className="pt-4">
          <CustomersCard partner={partner} />
        </TabsContent>
      </Tabs>
    </>
  );
}

function CustomersCard({
  partner,
}: {
  partner: NonNullable<ReturnType<typeof usePartner>['data']>;
}) {
  if (partner.organizations.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>No customers yet</CardTitle>
          <CardDescription>
            Create an organisation and set its partner to {partner.name} or to a sub-dealer under
            it; it can then take devices from this pool.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {partner.organizations.map((org) => (
        <Card key={org.id}>
          <CardContent className="flex items-center justify-between gap-4 pt-6">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                <Building2 className="h-5 w-5 text-primary" />
              </div>
              <div className="min-w-0">
                <Link
                  to="/organizations/$organizationId"
                  params={{ organizationId: org.id }}
                  className="font-medium hover:underline"
                >
                  {org.name}
                </Link>
                <p className="text-sm text-muted-foreground">
                  {org.stats.vehicles} unit(s) · {org.stats.users} user(s)
                  {org.isOwn ? '' : ` · via ${org.partner?.name}`}
                </p>
              </div>
            </div>
            <Badge variant={org.isActive ? 'success' : 'secondary'}>
              {org.isActive ? 'Active' : 'Inactive'}
            </Badge>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
